import { gql }                                                                                                                                             from 'apollo-boost';
import { ACTIVITY_STATUS, projectDescFields, projectTimelineFields, projectVersionDetailFields }                                                           from '@src/constants';
import { getFieldIfValueOrDefault, getFieldWithProjectIdAccessor, getFieldWithProjectVersionIdAccessor, getStaticVersionedField, maybeStaticVersionField } from "@src/util/network/QueryUtility";
import { SystemQueries }                                                                                                                                   from './SystemQueries';
import { Uuid }                                                                                                                                            from "@src/types";


const _projectTimelineFields      = ( projectVersionId = null ) => projectTimelineFields.map( field => getFieldWithProjectVersionIdAccessor( projectVersionId, field ) ).join( ' ' ),
      _projectDescFields          = projectDescFields.join( ' ' ),
      _projectVersionDetailFields = projectVersionDetailFields.join( ' ' );

export const fragments = {

    PROJECTS: {
        QUERY_RETURN:                        `id name isActive`,
        MUTATION_RETURN:                     `id name isActive projectVersions { ${ _projectVersionDetailFields } } isCorvalManaged isDiscoveryComplete`,
        CONFIG_FIELDS:                       `id name isActive projectVersions { ${ _projectVersionDetailFields } } isCorvalManaged isTemplateSource isDiscoveryComplete termDictionary businessFunctions { id name description colorValue iconName resourceCode isArchived }`,
        EDIT_FIELDS:                         ( projectVersionId = null ) => `
			id
			name
			${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'customStartDateUtc' ) }
			${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'customTimingMilestones' ) }{ 
			    id name date description 
            }
			${ _projectTimelineFields( projectVersionId ) }
			${ _projectDescFields }
			organizationId
			assetId
			asset{
			    organizationId
			}
			projectMembers{
				accountId
				projectRole
			}
			isActive
			isTemplateSource
			termDictionary
			revenue
		`,
        ASSUMPTIONS_FIELDS:                  `
			${ _projectTimelineFields() }
			${ _projectDescFields }
			organizationId
		`,
        DETAIL_FIELDS:                       `id name`,
        ASSET_DESC_FIELDS:                   _projectDescFields,
        VERSION_DETAIL_FIELDS:               _projectVersionDetailFields,
        TIMELINE_BOUNDARY_FIELDS:            ( projectVersionId = null ) => ( `
            ${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'mapLowerBound' ) }
            ${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'mapUpperBound' ) }
            `
        ),
        CUSTOM_START_DATE_FIELD:             ( projectVersionId = null ) => getFieldWithProjectVersionIdAccessor( projectVersionId, 'customStartDateUtc' ),
        DEFINED_TIMINGS:                     ( projectVersionId = null ) => getFieldWithProjectVersionIdAccessor( projectVersionId, 'customTimingMilestones' ),
        TIMELINE_FIELDS:                     ( projectVersionId = null ) => _projectTimelineFields( projectVersionId ),
        TIMELINE_FIELDS_AND_DEFINED_TIMINGS: ( projectVersionId = null ) => ( `
            ${ _projectTimelineFields( projectVersionId ) }
            ${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'customStartDateUtc' ) } 
            ${ getFieldWithProjectVersionIdAccessor( projectVersionId, 'customTimingMilestones' ) } { 
                id name date description 
            }`
        )
    },

    PROJECT_VERSIONS: {
        QUERY_RETURN:    `id name description`,
        MUTATION_RETURN: `id projectId name description`,
        EDIT_FIELDS:     `id projectId name description isActiveVersion`
    },

    PROJECT_ANNUAL_OBJECTIVES: {
        MUTATION_RETURN: `projectId year description`
    },

    PROJECT_EXPORT_DATA_REPORT: {
        MUTATION_RETURN: `id projectId reportFormatType reportUrl createdUtc createdBy{ firstName lastName } targetDataSets`
    },

    PROJECT_HIRES: {
        QUERY_RETURN: ( projectId?: string, projectVersionId = null ) => `
			id
			hires {
				id
				headcount
				salary
				modifier
				hireDate				
				${ getFieldIfValueOrDefault( projectVersionId, 'projectVersionMilestoneId', 'projectTimingMilestoneOverride' ) }
                ${ getFieldIfValueOrDefault( projectVersionId, 'projectVersionMilestoneOffsetMonths', 'projectTimingMilestoneOverrideOffsetMonths' ) }					
				hireType {
					${ getFieldWithProjectIdAccessor( projectId, 'name' ) }
					resourceCode
					businessFunction { id }
				}
				${ projectVersionId

                   ? `staticProjectActivity {
                        id
                        name
                        businessFunction { id }
                        activity {
                            id
                        }
                    }`

                   : `projectActivity{
                        id
                        activity {
                            displayName
                            businessFunction { id }
                        }
                    }`
        }
			}`
    },

    PROJECT_HIRE_TYPES: {
        QUERY_RETURN: ( projectId?: string, projectVersionId = null ) => `
			id
			${ projectVersionId ? getFieldWithProjectVersionIdAccessor( projectVersionId, 'projectVersionHireTypes' ) : 'hireTypes' } {
				hireTypeId
				salary
				modifier
				note
				
				name
				nameOverride
				
				description
				descriptionOverride
				
				recommendedSalary
				recommendedModifier
				
				businessFunctionId
				businessFunction { id name resourceCode colorValue }
				
				hires {
					id
					headcount
					isActive
					hireDate
					note
					
					${ getFieldIfValueOrDefault( projectVersionId, 'projectVersionMilestoneId', 'projectTimingMilestoneOverride' ) }
					${ getFieldIfValueOrDefault( projectVersionId, 'projectVersionMilestoneOffsetMonths', 'projectTimingMilestoneOverrideOffsetMonths' ) }
					${ maybeStaticVersionField( 'projectActivityId', projectVersionId ) }					
					${ projectVersionId

                       ? `staticProjectActivity {
                            id
                            name
                            subObjectiveId
                            businessFunctionId
                            businessFunction { id name resourceCode colorValue }
                            activity {
                                id
                                hires {
                                    hireTypeId
                                }
                            }
                            requiredHires{
                                headcount
                                hireTypeId
                            } 
                        }`

                       : `projectActivity {
                            id
                            subObjectiveId
                            activity {
                                id
                                displayName
                                businessFunctionId
                                businessFunction { id name resourceCode colorValue }
                                hires {
                                    hireTypeId
                                }
                            }
                            requiredHires{
                                headcount
                                hireTypeId
                            }
                            hiresOverride{
                                headcount
                                hireTypeId
                            }
                            hiresOverrideEnabled    
                        }` }
				}
				hireType{
					id
					${ getFieldIfValueOrDefault( !projectVersionId, getFieldWithProjectIdAccessor( projectId, 'name' ), 'name' ) }
					${ getFieldIfValueOrDefault( !projectVersionId, getFieldWithProjectIdAccessor( projectId, 'description' ), 'description' ) }
					resourceCode
					businessFunctionId
					businessFunction { id name resourceCode colorValue }
					managingHireType {
						${ getFieldIfValueOrDefault( !projectVersionId, getFieldWithProjectIdAccessor( projectId, 'name' ), 'name' ) }
					}
				}
			}`
    },

    PROJECT_HIRE_TYPE: {
        MUTATION_RETURN: ( projectId?: string, projectVersionId = null ) => `
            hireTypeId
            salary
            modifier
            recommendedSalary
            recommendedModifier
            businessFunction { id }
            hires {
                id
                hireDate
                headcount
                isActive
                note
                ${ maybeStaticVersionField( 'projectActivityId', projectVersionId ) }
                ${ projectVersionId

                   ? `staticProjectActivity {
                        id
                        name
                        businessFunction { id }
                        activity { 
                            id 
                        }
                      }`

                   : `projectActivity {
                        id
                        activity {
                            id
                            displayName
                            businessFunction { id }
                        }
                      }
                ` }
            }
            hireType{
                id
                ${ getFieldWithProjectIdAccessor( projectId, 'name' ) }
                resourceCode
                businessFunction { id }
            }`
    },

    PROJECT_FEES: {
        QUERY_RETURN: `id professionalFees { id feeDate amount professionalFee { name resourceCode } projectActivity{ id activity{ displayName businessFunction { id } } } }`
    },

    PROJECT_WORKFLOWS: {
        QUERY_RETURN: `
			id
			projectWorkFlowTasks {
				id
				assignedAccount { id firstName lastName businessFunctions { id } }
				updatedBy{ id firstName lastName businessFunctions }
				updatedUtc
				completedUtc
				workflowActionStatus
				workflowTask {
					id
					name
					ordinal
					description
					workflowActionType
					workflowContextType
				}
				${ SystemQueries.fragments.AUDIT_RECORDS.QUERY_RETURN }
			}`,

        TASK_RETURN: `
			id
			assignedAccount { id firstName lastName businessFunctions }
			updatedBy{ id firstName lastName businessFunctions }
			updatedUtc
			completedUtc
			workflowActionStatus
			workflowTask {
				id
				name
				ordinal
				description
				workflowActionType
				workflowContextType
			}
			${ SystemQueries.fragments.AUDIT_RECORDS.QUERY_RETURN }`,

        TASK_DEFINITION_RETURN: `
			id
			name
			ordinal
			description
			workflowActionType
			workflowContextType
		`
    },

    PROJECT_TEMPLATES: {
        QUERY_RETURN: `
            id
			category
            description
            icon
            isArchived
            name
            sourceProjectId
            sourceProjectVersionId
            project{ id organizationId projectMembers { accountId projectRole } }
            organizations { organizationId }
            milestones { id name milestoneDate milestoneType }
            tags { tag }`,

        MUTATION_RETURN: `
            id
			category
            description
            icon
            isArchived
            name
            organizations { organizationId }
            milestones { id name milestoneDate milestoneType }
            tags { tag }`
    },

    PROJECT_BUDGET_FEE_ITEMS: {
        QUERY_RETURN: ( projectId?: Uuid, projectVersionId = null ) => `
        
                    ${ projectVersionId
                       ? `${ getStaticVersionedField( 'budget' ) }(
                            includeRecurrenceActivities: true,
                            projectVersionId: "${ projectVersionId }",
                            completionStateIn: [
                                ${ ACTIVITY_STATUS.NOTSTARTED },
                                ${ ACTIVITY_STATUS.INCOMPLETE },
                                ${ ACTIVITY_STATUS.COMPLETE },
                            ]
                        )`
                       : `budget(
                            includeRecurrenceActivities: true,
                            completionStateIn:           [
                                ${ ACTIVITY_STATUS.NOTSTARTED },
                                ${ ACTIVITY_STATUS.INCOMPLETE },
                                ${ ACTIVITY_STATUS.COMPLETE },
                            ]
                       )` }{
                        
						feeBasedCostOccurrences{
							costOccurrence
							costAmount
							costAmountRange{ min max }
							costAmountType
							instanceType
							itemData{
								__typename
								
								${ !projectVersionId ? `... on ProjectActivity{
									id
									requiredHires {
									    hireTypeId
									}
						            startTimeUtc
						            endTimeUtc
						            subObjectiveId
						            operationalCostOverride
									operationalCostOverrideNote
									ongoingCostOverride
									ongoingCostOverrideNote
									isRecurrence
									recurrenceSourceProjectActivityId
									recurrenceValue
									recurrenceUnit
									recurrenceType
									recurrenceMilestones {
										recurrenceMilestone
									}
									professionalFees{
										professionalFeeId
										professionalFee{ id name amount resourceCode }
									}
									activity{ 
									    id 
									    displayName 
									    businessFunction { id }
										defaultOperationalCost
										defaultOngoingCostMin
										defaultOngoingCostMax
										defaultOngoingCostDurationQuarters
										hires{ hireTypeId headcount
											hireType{
											    id
											    ${ getFieldWithProjectIdAccessor( projectId, 'name' ) }
											    resourceCode
											    businessFunction { id }
                                            }
										}
										professionalFees{
											professionalFeeId
											professionalFee{ id name amount resourceCode }
										}
										tags{
											tag{ name }
										}
									}
								}` : `` }
								
								${ !projectVersionId ? `... on ProjectBudgetItem{
									id 
									name 
									categoryId 
									subCategoryName 
									businessFunction { id }
									values{ 
									    id 
									    value 
									    startTimeUtc 
                                    }
								}` : `` }	
								
								${ projectVersionId ? `... on StaticProjectActivity{		
															
									id
									name
									businessFunction { id }
						            
						            startDate
						            endDate
						            
						            subObjectiveId
						            subObjectiveName
									
									requiredHires {
									    headcount
									    hireTypeId
									    hireType{
                                            id
                                            name
                                            resourceCode
                                            businessFunction { id }
                                        }
									}
						            
						            operationalCost
									operationalCostNote
									operationalCostReplacesProfessionalFees
																		
									ongoingCostQuarters
									ongoingCostPerQuarter
									ongoingCostNote									
									
									scheduledBudgetItems{
                                        id
                                        amount
                                        name
                                        note
                                        year
                                        quarter
									}
									
									professionalFees{
										id
										name
										amount
										resourceCode
										professionalFeeId
									}
									
									activity{ 
									    id
									}
								}` : '' }
								
								${ projectVersionId ? `... on StaticProjectBudgetItem {
									id 
									name 
									categoryId 
									subCategoryName 
									businessFunction { id } 
									values{ 
									    id 
									    value 
									    startDate 
                                    }
								}` : `` }	
                            }
						}
					}
		      `
    },

    PROJECT_BUDGET_RESOURCING_ITEMS: {
        QUERY_RETURN: ( projectId?: string, projectVersionId = null ) => `
        
                        ${ projectVersionId
                           ? `${ getStaticVersionedField( 'budget' ) }(
                               projectVersionId: "${ projectVersionId }",
                               includeRecurrenceActivities: true
                           )`
                           : `budget( 
                                includeRecurrenceActivities: true 
                           )` }{
                       
						resourceCostOccurrences{
							hireTypeId
							hireTypeBusinessFunctionId
							hireTypeBusinessFunction { id name colorValue resourceCode }
							resourceCostOccurrence
							isInitialHireOccurrence
							quarterlyCompensation
							${ maybeStaticVersionField( 'projectHire', projectVersionId ) }{
								id
								note
								hireDate
								headcount
								salary
								modifier
								hireType {
										id
										${ getFieldIfValueOrDefault( !projectVersionId, getFieldWithProjectIdAccessor( projectId, 'name' ), 'name' ) }
                                        ${ getFieldIfValueOrDefault( !projectVersionId, getFieldWithProjectIdAccessor( projectId, 'description' ), 'description' ) }
										resourceCode
										businessFunction { id name colorValue iconName resourceCode }
										managingHireType{
						                    ${ getFieldIfValueOrDefault( !projectVersionId, getFieldWithProjectIdAccessor( projectId, 'name' ), 'name' ) }
						                }
									}
									${ projectVersionId

                                       ? `staticProjectActivity {
                                             id
                                             name
                                             businessFunction { id name colorValue iconName resourceCode }
                                             categoryId
                                             categoryName
                                             subCategoryName
                                             subCategoryId
                                             
                                             requiredHires{
                                                 headcount
                                                 hireTypeId
                                                 hireType {
                                                     id
                                                     ${ getFieldIfValueOrDefault( !projectVersionId, getFieldWithProjectIdAccessor( projectId, 'name' ), 'name' ) }
                                                     resourceCode
                                                     businessFunction { id name colorValue iconName resourceCode }
                                                 }
                                             }
                                             activity { 
                                                 id 
                                             }
                                        }`

                                       : `projectActivity{
                                            id
                                            activity {
                                                id
                                                displayName
                                                category{ id displayName }
                                                subCategory{ id displayName }
                                                businessFunction { id name colorValue iconName resourceCode }
                                                hires{
                                                    hireTypeId
                                                    headcount
                                                    hireType{
                                                        id
                                                        ${ getFieldIfValueOrDefault( !projectVersionId, getFieldWithProjectIdAccessor( projectId, 'name' ), 'name' ) }
                                                        resourceCode
                                                        businessFunction { id name colorValue iconName resourceCode }
                                                    }
                                                }
                                            }
                                        }` }
							}
						}
					}
		      `
    },

    PROJECT_CUSTOM_BUDGET_ITEMS: {
        QUERY_RETURN: ( projectVersionId = null ) => `
            id 
            name 
            categoryId 
            subCategoryName 
            businessFunction { id name colorValue iconName resourceCode }
            values{ 
                id 
                value 
                ${ getFieldIfValueOrDefault( projectVersionId, 'startDate', 'startTimeUtc' ) } 
            }`
    }
};

export const queries = {

    /* PROJECTS
     * --------------------------------------------------------------------------------------------*/
    GET_PROJECTS: ( returnQueryString = fragments.PROJECTS.QUERY_RETURN ) => {

        const query = `
            query GetProjects( $order: [ProjectSortInput!], $where: ProjectFilterInput ){
                projects( order: $order, where: $where)  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    GET_PROJECT: ( returnQueryString = fragments.PROJECTS.QUERY_RETURN ) => {

        const query = `
            query GetProject( $id: Uuid! ){
                projects( where: { id: { eq: $id } } )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    GET_PROJECT_HIRES: ( returnQueryString = fragments.PROJECT_HIRES.QUERY_RETURN() ) => {

        const query = `
            query GetProjectHires( $id: Uuid! ){
                projects( where: { id: { eq: $id } } )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    GET_PROJECT_HIRE_TYPES: ( returnQueryString = fragments.PROJECT_HIRE_TYPES.QUERY_RETURN() ) => {

        const query = `
            query GetProjectHireTypes( $id: Uuid! ){
                projects( where: { id: { eq: $id } } )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    GET_PROJECT_WORKFLOWS: ( returnQueryString = fragments.PROJECT_WORKFLOWS.QUERY_RETURN ) => {

        const query = `
            query GetProjectWorkflows( $id: Uuid! ){
                projects( where: { id: { eq: $id } } )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    GET_PROJECT_TEMPLATES: ( returnQueryString = fragments.PROJECT_TEMPLATES.QUERY_RETURN ) => {

        const query = `
            query GetProjectTemplates {
                me {
                    id
                    projectTemplates {
                        ${ returnQueryString }
                    }
                }
            }`;

        return gql( query );
    }
};

export const mutations = {

    /* PROJECTS
     * --------------------------------------------------------------------------------------------*/
    CREATE_PROJECT: ( returnQueryString = fragments.PROJECTS.MUTATION_RETURN ) => {

        const query = `
            mutation CreateProject( $input: ProjectCreateCommandInput! ){
                createProject( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    CREATE_PROJECT_FROM_TEMPLATE: ( returnQueryString = fragments.PROJECTS.MUTATION_RETURN ) => {

        const query = `
            mutation CreateProjectFromTemplate( $input: ProjectCreateFromTemplateCommandInput! ){
                createProjectFromTemplate( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    CREATE_PROJECT_TEMPLATE: ( returnQueryString = fragments.PROJECT_TEMPLATES.MUTATION_RETURN ) => {

        const query = `
            mutation CreateProjectTemplate( $input: ProjectTemplateCreateCommandInput! ){
                createProjectTemplate( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_TEMPLATE: ( returnQueryString = fragments.PROJECT_TEMPLATES.MUTATION_RETURN ) => {

        const query = `
            mutation UpdateProjectTemplate( $input: ProjectTemplateUpdateCommandInput! ){
                updateProjectTemplate( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    CREATE_PROJECT_VERSION: ( returnQueryString = fragments.PROJECT_VERSIONS.MUTATION_RETURN ) => {

        const query = `
            mutation CreateProjectVersion($input: ProjectVersionCreateCommandInput!) {
                createProjectVersion(input: $input) {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_VERSION: ( returnQueryString = fragments.PROJECT_VERSIONS.MUTATION_RETURN ) => {

        const query = `
            mutation UpdateProjectVersion($input: ProjectVersionUpdateCommandInput!) {
                updateProjectVersion(input: $input) {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    DELETE_PROJECT_VERSION: () => {

        const query = `
            mutation DeleteProjectVersion($input: ProjectVersionDeleteCommandInput!) {
                deleteProjectVersion(input: $input)
            }`;

        return gql( query );
    },


    UPDATE_PROJECT: ( returnQueryString = fragments.PROJECTS.MUTATION_RETURN ) => {

        const query = `
            mutation UpdateProject( $input: ProjectUpdateCommandInput! ){
                updateProject( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    DUPLICATE_PROJECT: ( returnQueryString = fragments.PROJECTS.MUTATION_RETURN ) => {

        const query = `
            mutation DuplicateProject( $input: ProjectCloneCommandInput! ) {
                cloneProject( input: $input ) {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    CREATE_PROJECT_ANNUAL_OBJECTIVE: ( returnQueryString = fragments.PROJECT_ANNUAL_OBJECTIVES.MUTATION_RETURN ) => {

        const query = `
            mutation CreateProjectAnnualObjective( $input: ProjectAnnualObjectiveCreateCommandInput! ){
                createProjectAnnualObjective( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_ANNUAL_OBJECTIVE: ( returnQueryString = fragments.PROJECT_ANNUAL_OBJECTIVES.MUTATION_RETURN ) => {

        const query = `
            mutation UpdateProjectAnnualObjective( $input: ProjectAnnualObjectiveUpdateCommandInput! ){
                updateProjectAnnualObjective( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_HIRE_TYPE: ( returnQueryString = fragments.PROJECT_HIRE_TYPE.MUTATION_RETURN(), projectVersionId = null ) => {

        const query = `
            mutation UpdateProjectHireType(
                $input: ${ getFieldIfValueOrDefault( projectVersionId, 'ProjectVersionHireTypeUpdateCommandInput!', 'ProjectHireTypeUpdateCommandInput!' ) }
            ){
                ${ getFieldIfValueOrDefault( projectVersionId, 'updateProjectVersionHireType', 'updateProjectHireType' ) }( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_BUDGET_ITEMS: ( returnQueryString = fragments.PROJECT_HIRE_TYPE.MUTATION_RETURN(), projectVersionId = null ) => {

        const query = `
            mutation SaveProjectBudgetItems( 
                $input: ${ getFieldIfValueOrDefault( projectVersionId, 'StaticProjectBudgetItemSaveCommandInput!', 'ProjectBudgetItemSaveCommandInput!' ) } 
            ){
                ${ getFieldIfValueOrDefault( projectVersionId, 'saveStaticProjectBudgetItems', 'saveProjectBudgetItems' ) }( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_WORKFLOW_TASK: ( returnQueryString = fragments.PROJECT_WORKFLOWS.TASK_RETURN ) => {

        const query = `
            mutation UpdateProjectWorkflowTask( $input: ProjectWorkflowTaskUpdateActionStatusCommandInput! ){
                updateProjectWorkflowTaskActionStatus( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    REASSIGN_PROJECT_WORKFLOW_TASK: ( returnQueryString = fragments.PROJECT_WORKFLOWS.TASK_RETURN ) => {

        const query = `
            mutation ReassignProjectWorkflowTask( $input: ProjectWorkflowTaskReassignCommandInput! ){
                reassignProjectWorkflowTask( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    EXPORT_PROJECT_DATA: ( returnQueryString = fragments.PROJECT_EXPORT_DATA_REPORT.MUTATION_RETURN ) => {

        const query = `
            mutation ExportProjectDataToFile( $input: ReportCreateCommandInput! ){
                createReport( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    REMAP_PROJECT: ( returnQueryString = fragments.PROJECTS.MUTATION_RETURN ) => {

        const query = `
            mutation RemapProject( $input: ProjectRemapCommandInput! ){
                remapProject( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_CONTEXT: ( returnQueryString = fragments.PROJECTS.MUTATION_RETURN ) => {

        const query = `
            mutation UpdateProjectManagementScheme( $input: ProjectUpdateCorvalManagedCommandInput! ){
                updateProjectCorvalManaged( input: $input )  {
                    ${ returnQueryString }
                }
            }`;

        return gql( query );
    },

    UPDATE_PROJECT_VERSION_DATA: () => {

        const query = `
            mutation UpdateProjectVersionData( $input: ProjectVersionUpdateDataCommandInput! ){
                updateProjectVersionData( input: $input ){
                    success
                    invalidActivityIds
                    invalidDependencyIds
                    invalidEventIds
                }
            }`;

        return gql( query );
    },
};

export const ProjectQueries = {
    fragments,
    queries,
    mutations,
};
